import { EMPTY_FIELD } from 'constants/emptyField';
import { DEFAULT_PAGINATION } from 'constants/pagination';

import { useEffect, useState } from 'react';

import ContentAdd from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { MerchantNotificationsFilters } from 'config/requests/merchants';
import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { ListContextProvider, ListControllerResult, useList } from 'react-admin';
import { useSearchParams } from 'react-router-dom';
import { Alert } from 'shared/mui/Alert/Alert';
import { Button } from 'shared/mui/Buttons';
import {
  ContextMenu,
  ContextMenuDeleteButton,
  ContextMenuEditDialogButton,
  ContextMenuShowDialogButton,
} from 'shared/mui/ContextMenu';
import { PageHeader } from 'shared/mui/PageHeader';
import { DatagridSkeleton } from 'shared/react-admin/Datagrids';
import { DateTimeField, TextField, TooltipTextField } from 'shared/react-admin/Fields';
import { ReferenceField } from 'shared/react-admin/ReferenceFields';
import { Merchant } from 'types/embedded/merchant';
import { MerchantNotification } from 'types/embedded/merchantNotification';
import { Permission } from 'types/embedded/permission';

import { useGetMerchantNotifications } from './hooks/useGetMerchantNotifications';
import { useGetMerchantNotificationsEventNames } from './hooks/useGetMerchantNotificationsEventNames';
import { StyledDatagrid, StyledPagination } from './MerchantsShowNotifications.styled';
import { MerchantsShowNotificationsCreateDialog } from './MerchantsShowNotificationsCreateDialog/MerchantsShowNotificationsCreateDialog';
import { MerchantsShowNotificationsDeleteDialog } from './MerchantsShowNotificationsDeleteDialog/MerchantsShowNotificationsDeleteDialog';
import { MerchantsShowNotificationsDeliveryChannelActivityField } from './MerchantsShowNotificationsDeliveryChannelActivityField/MerchantsShowNotificationsDeliveryChannelActivityField';
import { MerchantsShowNotificationsEditDialog } from './MerchantsShowNotificationsEditDialog/MerchantsShowNotificationsEditDialog';
import { MerchantsShowNotificationsReferenceDialog } from './MerchantsShowNotificationsReferenceDialog/MerchantsShowNotificationsReferenceDialog';
import { MerchantsShowNotificationsSendNotificationDialog } from './MerchantsShowNotificationsSendNotificationDialog/MerchantsShowNotificationsSendNotificationDialog';
import { MerchantsShowNotificationsShowDialog } from './MerchantsShowNotificationsShowDialog/MerchantsShowNotificationsShowDialog';
import { MerchantsShowActionSkeleton } from '../components/MerchantsShowActionSkeleton';

type MerchantsShowNotificationsProps = {
  record?: Merchant;
};

export type MerchantsShowNotificationsDialogsType = 'show' | 'edit' | null;

export const MerchantsShowNotifications: React.FC<MerchantsShowNotificationsProps> = ({
  record,
}) => {
  const translate = useTypedTranslate();

  const [searchParams, setSearchParams] = useSearchParams();
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [filters, setFilters] = useState<MerchantNotificationsFilters>(DEFAULT_PAGINATION);
  const [previousDialog, setPreviousDialog] = useState<MerchantsShowNotificationsDialogsType>(null);
  const [isOpenReferenceDialog, setIsOpenReferenceDialog] = useState(false);
  const { eventNames } = useGetMerchantNotificationsEventNames();

  const { merchantNotifications, isLoading, totalItems, isFetching } = useGetMerchantNotifications({
    filters,
    merchantId: record?.id,
  });

  const listContext = useList({
    data: merchantNotifications || [],
    isFetching,
    isLoading,
    page: filters.page,
    perPage: filters.itemsPerPage,
  });
  const { hasResourcePermissions } = usePermissions();

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      page: Number(searchParams.get('page')) || DEFAULT_PAGINATION.page,
      itemsPerPage: Number(searchParams.get('itemsPerPage')) || DEFAULT_PAGINATION.itemsPerPage,
    }));
  }, [searchParams]);

  const {
    record: recordEditDialog,
    handleCloseRecordModal: handleCloseEditDialog,
    openRecordModal: openEditDialog,
    handleOpenRecordModal: handleOpenEditDialog,
  } = useRecordModal<MerchantNotification>();
  const {
    record: recordShowDialog,
    handleCloseRecordModal: handleCloseShowDialog,
    openRecordModal: openShowDialog,
    handleOpenRecordModal: handleOpenShowDialog,
  } = useRecordModal<MerchantNotification>();
  const {
    record: recordDeleteDialog,
    handleCloseRecordModal: handleCloseDeleteDialog,
    openRecordModal: openDeleteDialog,
    handleOpenRecordModal: handleOpenDeleteDialog,
  } = useRecordModal<MerchantNotification>();
  const {
    record: recordSendNotificationDialog,
    handleCloseRecordModal: handleCloseSendNotificationDialog,
    openRecordModal: openSendNotificationDialog,
    handleOpenRecordModal: handleOpenSendNotificationDialog,
  } = useRecordModal<MerchantNotification>();

  const handleCloseNotificationDialog = () => {
    handleCloseSendNotificationDialog();
    handleOpenShowDialog(recordSendNotificationDialog);
  };

  const deleteDialogClose = () => {
    if (previousDialog === 'show') {
      handleOpenShowDialog(recordDeleteDialog);
    }
    if (previousDialog === 'edit') {
      handleOpenEditDialog(recordDeleteDialog);
    }
    handleCloseDeleteDialog();
  };

  return (
    <ListContextProvider
      value={
        {
          ...listContext,
          page: filters.page || 1,
          perPage: filters.itemsPerPage || 10,
          setPage: (page) => {
            setSearchParams((prev) => {
              return { ...Object.fromEntries(prev), page: page.toString() };
            });
            setFilters((prev) => ({ ...prev, page: page }));
          },
          setPerPage: (itemsPerPage) => {
            setFilters((prev) => ({ ...prev, itemsPerPage: itemsPerPage }));
            setSearchParams((prev) => {
              return { ...Object.fromEntries(prev), itemsPerPage: itemsPerPage.toString() };
            });
          },
          total: totalItems,
        } as ListControllerResult
      }
    >
      <PageHeader
        actions={[
          isLoading ? (
            <MerchantsShowActionSkeleton />
          ) : (
            <Button
              key="help"
              label={translate('app.buttons.info')}
              onClick={() => setIsOpenReferenceDialog(true)}
              startIcon={<InfoOutlinedIcon />}
              variant="outlined"
            />
          ),
          hasResourcePermissions('merchant_notification_settings', [
            Permission.Full,
            Permission.Create,
          ]) &&
            (isLoading ? (
              <MerchantsShowActionSkeleton />
            ) : (
              <Button
                key="create"
                label={translate('app.buttons.add')}
                onClick={() => setOpenCreateDialog(true)}
                startIcon={<ContentAdd />}
                variant="contained"
              />
            )),
        ]}
        headerTitle={{
          titleText: translate('app.merchantsShowNotifications.notificationSettings'),
          titleSize: 'smallBold',
        }}
      />
      {!totalItems && !isLoading ? (
        <Alert severity="info">
          {translate('app.merchantsShowNotifications.noNotificationSettings', {
            name: record?.name,
          })}
        </Alert>
      ) : isLoading ? (
        <DatagridSkeleton columnsCount={10} />
      ) : (
        <>
          <StyledDatagrid bulkActionButtons={false} preferenceKey="merchant_notifications">
            <TextField label="ID" sortBy="id" sortable={false} source="id" />
            {hasResourcePermissions('merchant_notification_settings', [
              Permission.Full,
              Permission.Update,
            ]) && (
              <MerchantsShowNotificationsDeliveryChannelActivityField
                label={EMPTY_FIELD}
                source="active"
              />
            )}
            <TextField
              cellClassName="column-credentials"
              headerClassName="column-credentials"
              label={translate('app.merchantsShowNotifications.channelId')}
              sortable={false}
              source="credentials.chatId"
            />
            <TooltipTextField
              emptyText={EMPTY_FIELD}
              label={translate('app.table.comment')}
              source="comment"
            />
            <ReferenceField
              cellClassName="column-provider"
              empty={EMPTY_FIELD}
              label={translate('app.table.provider')}
              reference="providers"
              sortable={false}
              source="provider.@id"
            >
              <TextField source="name" sx={{ textDecoration: 'underline' }} />
            </ReferenceField>
            <DateTimeField
              label={translate('app.table.createdAt')}
              sortable={false}
              source="createdAt"
            />
            <DateTimeField
              label={translate('app.table.updatedAt')}
              sortable={false}
              source="updatedAt"
            />
            <ContextMenu
              render={(record: MerchantNotification) => [
                hasResourcePermissions('merchant_notification_settings', [
                  Permission.Full,
                  Permission.Read,
                ]) && (
                  <ContextMenuShowDialogButton
                    key="show-button"
                    onClick={() => handleOpenShowDialog(record)}
                  />
                ),
                hasResourcePermissions('merchant_notification_settings', [
                  Permission.Full,
                  Permission.Update,
                ]) && (
                  <ContextMenuEditDialogButton
                    key="edit-button"
                    onClick={() => handleOpenEditDialog(record)}
                  />
                ),
                hasResourcePermissions('merchant_notification_settings', [
                  Permission.Full,
                  Permission.Delete,
                ]) && (
                  <ContextMenuDeleteButton
                    key="delete-button"
                    onClick={() => handleOpenDeleteDialog(record)}
                  />
                ),
              ]}
            />
          </StyledDatagrid>
          <StyledPagination />
        </>
      )}
      {openCreateDialog && (
        <MerchantsShowNotificationsCreateDialog
          eventNames={eventNames}
          merchantId={record?.id as string}
          onClose={() => setOpenCreateDialog(false)}
          open={openCreateDialog}
        />
      )}
      {openShowDialog && recordShowDialog && (
        <MerchantsShowNotificationsShowDialog
          eventNames={eventNames}
          onClose={() => {
            setPreviousDialog(null);
            handleCloseShowDialog();
          }}
          onDelete={() => {
            handleOpenDeleteDialog(recordShowDialog);
            setPreviousDialog('show');
          }}
          onEdit={handleOpenEditDialog}
          onSendNotification={() => handleOpenSendNotificationDialog(recordShowDialog)}
          open={openShowDialog}
          record={recordShowDialog}
        />
      )}
      {openEditDialog && recordEditDialog && (
        <MerchantsShowNotificationsEditDialog
          eventNames={eventNames}
          merchantId={record?.id as string}
          onClose={() => {
            setPreviousDialog(null);
            handleCloseEditDialog();
          }}
          onDelete={() => {
            handleOpenDeleteDialog(recordEditDialog);
            setPreviousDialog('edit');
          }}
          open={openEditDialog}
          record={recordEditDialog}
        />
      )}
      {openDeleteDialog && recordDeleteDialog && (
        <MerchantsShowNotificationsDeleteDialog
          onClose={deleteDialogClose}
          onCloseSuccess={handleCloseDeleteDialog}
          open={openDeleteDialog}
          record={recordDeleteDialog}
        />
      )}
      {openSendNotificationDialog && recordSendNotificationDialog && (
        <MerchantsShowNotificationsSendNotificationDialog
          onClose={handleCloseNotificationDialog}
          open={openSendNotificationDialog}
        />
      )}
      {isOpenReferenceDialog && (
        <MerchantsShowNotificationsReferenceDialog
          onClose={() => setIsOpenReferenceDialog(false)}
          open={isOpenReferenceDialog}
        />
      )}
    </ListContextProvider>
  );
};
